(() => {
  if (customElements.get("p-expand-collapse")) return;

  class PExpandCollapse extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll("[data-accordion-trigger]"));
      this.panels = Array.from(this.querySelectorAll("[data-accordion-panel]"));
      if (!this.triggers.length || !this.panels.length) return;
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.boundClick = this.handleTriggerClick.bind(this);
      this.triggers.forEach((trigger) => trigger.addEventListener("click", this.boundClick));
      this.syncPanelStateFromMarkup();

      const hasExpanded = this.triggers.some((trigger) => trigger.getAttribute("aria-expanded") === "true");
      if (!hasExpanded && this.dataset.openFirst === "true") {
        this.setActive(this.triggers[0]);
      }
    }

    disconnectedCallback() {
      if (!this.triggers || !this.boundClick) return;
      this.triggers.forEach((trigger) => trigger.removeEventListener("click", this.boundClick));
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      if (!(trigger instanceof HTMLElement)) return;

      const isExpanded = trigger.getAttribute("aria-expanded") === "true";
      if (isExpanded) {
        this.closeItem(trigger);
        return;
      }
      this.setActive(trigger);
    }

    setActive(activeTrigger) {
      this.triggers.forEach((trigger) => {
        if (trigger === activeTrigger) {
          this.openItem(trigger);
        } else {
          this.closeItem(trigger);
        }
      });
    }

    openItem(trigger) {
      const panel = this.getPanelByTrigger(trigger);
      if (!panel) return;

      trigger.setAttribute("aria-expanded", "true");
      if (this.reduceMotion) {
        panel.hidden = false;
        panel.style.height = "auto";
        panel.style.opacity = "1";
        return;
      }

      panel.hidden = false;
      panel.style.height = "0px";
      panel.style.opacity = "0";
      const targetHeight = panel.scrollHeight;

      requestAnimationFrame(() => {
        panel.style.height = `${targetHeight}px`;
        panel.style.opacity = "1";
      });

      this.bindTransitionEnd(panel, () => {
        if (trigger.getAttribute("aria-expanded") === "true") {
          panel.style.height = "auto";
        }
      });
    }

    closeItem(trigger) {
      const panel = this.getPanelByTrigger(trigger);
      if (!panel) return;

      trigger.setAttribute("aria-expanded", "false");
      if (this.reduceMotion) {
        panel.style.height = "0px";
        panel.style.opacity = "0";
        panel.hidden = true;
        return;
      }

      const startHeight = panel.scrollHeight;
      panel.hidden = false;
      panel.style.height = `${startHeight}px`;
      panel.style.opacity = "1";

      requestAnimationFrame(() => {
        panel.style.height = "0px";
        panel.style.opacity = "0";
      });

      this.bindTransitionEnd(panel, () => {
        if (trigger.getAttribute("aria-expanded") === "false") {
          panel.hidden = true;
        }
      });
    }

    getPanelByTrigger(trigger) {
      const panelId = trigger.getAttribute("aria-controls");
      if (!panelId) return null;
      return this.querySelector(`#${CSS.escape(panelId)}`);
    }

    bindTransitionEnd(panel, callback) {
      if (panel._panelTransitionHandler) {
        panel.removeEventListener("transitionend", panel._panelTransitionHandler);
      }

      panel._panelTransitionHandler = (event) => {
        if (event.propertyName !== "height") return;
        callback();
      };
      panel.addEventListener("transitionend", panel._panelTransitionHandler, { once: true });
    }

    syncPanelStateFromMarkup() {
      this.triggers.forEach((trigger) => {
        const panel = this.getPanelByTrigger(trigger);
        if (!panel) return;
        const expanded = trigger.getAttribute("aria-expanded") === "true";
        if (expanded) {
          panel.hidden = false;
          panel.style.height = "auto";
          panel.style.opacity = "1";
        } else {
          panel.hidden = true;
          panel.style.height = "0px";
          panel.style.opacity = "0";
        }
      });
    }
  }

  customElements.define("p-expand-collapse", PExpandCollapse);
})();
